import { useQuery } from '@apollo/client';
import { faServer } from '@fortawesome/pro-solid-svg-icons';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { slimRenderOption } from './slimRenderOption.tsx';
import { SourceSyncStatusLine } from './SourceSyncStatusLine.tsx';
import SelectField from '../../../../core/components/Fields/SelectField.tsx';
import StringField from '../../../../core/components/Fields/StringField.tsx';
import FormField from '../../../../core/components/FormField.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import Switch from '../../../../core/components/Switch.tsx';
import classnames from '../../../../core/utils/classnames.tsx';
import { useControllableState } from '../../../../core/utils/useControllableState.tsx';
import { GET_AVAILABLE_SFTP_CONFIGS } from '../../queries.ts';
import { SftpKnowledgeSourceConfig, SFTP_KNOWLEDGE_SOURCE_DEFAULT } from '../config/ConfigSteps.tsx';

export const SftpKnowledgeSourceCard = ({
    isChecked,
    disabled,
    readOnly,
    onIsCheckedChange,
    config: propsConfig,
    onConfigChange: propsOnConfigChange,
    className,
    ...props
}: React.ComponentPropsWithRef<'div'> & {
    isChecked: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    onIsCheckedChange: (checked: boolean) => void;
    config?: SftpKnowledgeSourceConfig | null;
    onConfigChange?: (config: SftpKnowledgeSourceConfig | null) => void;
}) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });

    const { data: sftpConfigsData, loading: sftpConfigsLoading } = useQuery(GET_AVAILABLE_SFTP_CONFIGS);
    const sftpConfigs = sftpConfigsData?.availableSftpConfigs ?? [];
    const sftpConfigOptions = sftpConfigs.map((c: { id: string; host: string; user: string }) => ({
        value: c.id,
        label: [c.user, c.host].filter(Boolean).join('@') || t('toolProviders.sftp.workistManaged'),
    }));

    const [config, setConfig] = useControllableState(
        { ...SFTP_KNOWLEDGE_SOURCE_DEFAULT },
        propsConfig,
        propsOnConfigChange
    );

    const handleToggle = (checked: boolean) => {
        onIsCheckedChange(checked);
        setConfig(
            checked
                ? { ...SFTP_KNOWLEDGE_SOURCE_DEFAULT, sftpConfigId: sftpConfigs.length === 1 ? sftpConfigs[0].id : '' }
                : null
        );
    };

    const handleConfigChange = (sftpConfigId: string) => {
        setConfig({ ...SFTP_KNOWLEDGE_SOURCE_DEFAULT, sftpConfigId });
    };

    const handlePathChange = (path: string) => {
        setConfig({ ...config!, path });
    };

    return (
        <div
            className={classnames(
                'rounded-lg flex flex-col gap-2 p-2 border border-line-weak border-solid bg-surface-weak transition-colors',
                disabled && 'opacity-50',
                className
            )}
            {...props}
        >
            <label className="flex flex-col cursor-pointer hover:bg-surface-medium group transition-colors rounded-lg">
                <div className="flex justify-between items-center p-2 pb-0">
                    <div className="flex gap-2 items-center">
                        <SftpIcon className="w-5 h-5 text-content-medium" />
                        <h3 className="text-sm font-medium text-content-strong">{t('toolProviders.sftp.name')}</h3>
                    </div>
                    {!readOnly && (
                        <Switch
                            value={isChecked}
                            onValueChange={handleToggle}
                            disabled={disabled || (!isChecked && sftpConfigs.length === 0)}
                        />
                    )}
                </div>
                <div className="text-sm text-content-medium px-2 pb-2">{t('toolProviders.sftp.description')}</div>
                {!readOnly && !sftpConfigsLoading && sftpConfigs.length === 0 && (
                    <div className="text-xs text-content-medium px-2 pb-2">{t('toolProviders.sftp.noConfigsHint')}</div>
                )}
            </label>

            {isChecked && config && (
                <div className="flex flex-col gap-4 bg-surface-medium rounded-lg p-2 px-3">
                    <SourceSyncStatusLine config={config} />
                    <FormField label={t('toolProviders.sftp.configLabel')}>
                        <SelectField
                            options={sftpConfigOptions}
                            placeholder={t('toolProviders.sftp.configPlaceholder')}
                            loadingPlaceholder={t('toolProviders.sftp.loading')}
                            isLoading={sftpConfigsLoading}
                            value={config.sftpConfigId}
                            onValueChange={handleConfigChange}
                            renderOption={slimRenderOption}
                            disabled={readOnly}
                            modal
                        />
                    </FormField>

                    {config.sftpConfigId && (
                        <FormField label={t('toolProviders.sftp.pathLabel')}>
                            <StringField
                                value={config.path}
                                onValueChange={handlePathChange}
                                placeholder={t('toolProviders.sftp.pathPlaceholder')}
                                disabled={readOnly}
                            />
                        </FormField>
                    )}
                </div>
            )}
        </div>
    );
};

const SftpIcon = withIcon(faServer);
